import {
  type EndpointUpdateProps,
  buildSslProtocolsOptions,
  endpointSettingApplies,
  fetchEndpoint,
  fetchImageById,
  fetchSettingById,
  getContainerPort,
  isRequiresCert,
  isTlsOrTcp,
  loadBalancingAlgorithms,
  parseIpStr,
  parsePortsStrToNum,
  selectAppById,
  selectEndpointById,
  selectImageById,
  selectServiceById,
  selectSettingById,
  updateEndpoint,
} from "@app/deploy";
import {
  useDispatch,
  useLoader,
  useLoaderSuccess,
  useQuery,
  useSelector,
} from "@app/react";
import { endpointDetailActivityUrl } from "@app/routes";
import { ipValidator, portValidator } from "@app/validator";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { useValidator } from "../hooks";
import {
  BannerMessages,
  Box,
  ButtonCreate,
  CertSelector,
  CheckBox,
  EndpointDeprovision,
  EndpointHealthchecksSettings,
  EndpointSecuritySettings,
  ExternalLink,
  FileDropTextArea,
  FormGroup,
  Group,
  Input,
  Select,
  type SelectOption,
} from "../shared";

const validators = {
  port: (data: EndpointUpdateProps) => {
    if (data.enpType !== "tls" && data.enpType !== "tcp") {
      return portValidator(data.containerPort);
    }
  },
  ports: (data: EndpointUpdateProps) => {
    if (data.enpType === "tls" || data.enpType === "tcp") {
      const errs: string[] = [];
      data.containerPorts.forEach((port) => {
        const result = portValidator(port.toString());
        if (result) errs.push(`[${port}] is not between 1 and 65535`);
      });
      if (errs.length === 0) return;
      return errs.join(", ");
    }
  },
  ipAllowlist: (data: EndpointUpdateProps) => ipValidator(data.ipAllowlist),
  cert: (data: EndpointUpdateProps) => {
    if (data.requiresCert && data.certId === "" && data.cert === "") {
      return "A certificate is required for custom HTTPS";
    }
  },
  privKey: (data: EndpointUpdateProps) => {
    if (data.requiresCert && data.certId === "" && data.privKey === "") {
      return "A private key is required for custom HTTPS";
    }
  },
  domain: (data: EndpointUpdateProps) => {
    if (
      data.shared &&
      data.requiresCert &&
      (!data.domain || data.domain.trim() === "")
    ) {
      return "A domain is required for shared endpoints with a certificate.";
    }
  },
};

const EndpointSettings = ({ endpointId }: { endpointId: string }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  useQuery(fetchEndpoint({ id: endpointId }));
  const enp = useSelector((s) => selectEndpointById(s, { id: endpointId }));
  useQuery(fetchSettingById({ id: enp.currentSettingId }));
  const setting = useSelector((s) =>
    selectSettingById(s, { id: enp.currentSettingId }),
  );
  const service = useSelector((s) =>
    selectServiceById(s, { id: enp.serviceId }),
  );
  const app = useSelector((s) => selectAppById(s, { id: service.appId }));
  useQuery(fetchImageById({ id: app.currentImageId }));
  const image = useSelector((s) =>
    selectImageById(s, { id: app.currentImageId }),
  );
  const exposedPorts = image.exposedPorts;
  const origAllowlist = enp.ipWhitelist.join("\n");
  const origContainerPorts = enp.containerPorts.join(", ");
  const [ipAllowlist, setIpAllowlist] = useState(origAllowlist);
  const [port, setPort] = useState(enp.containerPort);
  const [ports, setPorts] = useState(origContainerPorts);
  const [certId, setCertId] = useState(enp.certificateId);
  const [cert, setCert] = useState("");
  const [privKey, setPrivKey] = useState("");
  const [tokenHeader, setTokenHeader] = useState(
    enp.tokenHeader as string | undefined,
  );
  const [lbAlgorithm, setLbAlgorithm] = useState(
    enp.lbAlgorithm as string | undefined,
  );
  const [shared, setShared] = useState(enp.shared);
  const [domain, setDomain] = useState(enp.userDomain);
  const [usingNewCert, setUsingNewCert] = useState(false);

  // Advanced settings state — initialized from setting.settings
  const s = setting.settings ?? {};
  const [idleTimeout, setIdleTimeout] = useState(s.IDLE_TIMEOUT ?? "");
  const [forceSSL, setForceSSL] = useState(s.FORCE_SSL ?? "");
  const [maintenancePageUrl, setMaintenancePageUrl] = useState(
    s.MAINTENANCE_PAGE_URL ?? "",
  );
  const [strictHealthChecks, setStrictHealthChecks] = useState(
    s.STRICT_HEALTH_CHECKS ?? "",
  );
  const [sslProtocolsOverride, setSslProtocolsOverride] = useState(
    s.SSL_PROTOCOLS_OVERRIDE ?? "",
  );
  const [sslCiphersOverride, setSslCiphersOverride] = useState(
    s.SSL_CIPHERS_OVERRIDE ?? "",
  );
  const [disableWeakCipherSuites, setDisableWeakCipherSuites] = useState(
    s.DISABLE_WEAK_CIPHER_SUITES ?? "",
  );
  const [showElbHealthchecks, setShowElbHealthchecks] = useState(
    s.SHOW_ELB_HEALTHCHECKS ?? "",
  );
  const [releaseHealthcheckTimeout, setReleaseHealthcheckTimeout] = useState(
    s.RELEASE_HEALTHCHECK_TIMEOUT ?? "",
  );

  useEffect(() => {
    setIpAllowlist(origAllowlist);
  }, [origAllowlist]);
  useEffect(() => {
    setPort(enp.containerPort);
  }, [enp.containerPort]);
  useEffect(() => {
    setPorts(origContainerPorts);
  }, [origContainerPorts]);
  useEffect(() => {
    setCertId(enp.certificateId);
  }, [enp.certificateId]);
  useEffect(() => {
    setTokenHeader(enp.tokenHeader);
  }, [enp.tokenHeader]);
  useEffect(() => {
    setLbAlgorithm(enp.lbAlgorithm);
  }, [enp.lbAlgorithm]);
  useEffect(() => {
    setShared(enp.shared);
  }, [enp.shared]);
  useEffect(() => {
    setDomain(enp.userDomain);
  }, [enp.userDomain]);
  useEffect(() => {
    setIdleTimeout(setting.settings?.IDLE_TIMEOUT ?? "");
  }, [setting.settings?.IDLE_TIMEOUT]);
  useEffect(() => {
    setForceSSL(setting.settings?.FORCE_SSL ?? "");
  }, [setting.settings?.FORCE_SSL]);
  useEffect(() => {
    setMaintenancePageUrl(setting.settings?.MAINTENANCE_PAGE_URL ?? "");
  }, [setting.settings?.MAINTENANCE_PAGE_URL]);
  useEffect(() => {
    setStrictHealthChecks(setting.settings?.STRICT_HEALTH_CHECKS ?? "");
  }, [setting.settings?.STRICT_HEALTH_CHECKS]);
  useEffect(() => {
    setSslProtocolsOverride(setting.settings?.SSL_PROTOCOLS_OVERRIDE ?? "");
  }, [setting.settings?.SSL_PROTOCOLS_OVERRIDE]);
  useEffect(() => {
    setSslCiphersOverride(setting.settings?.SSL_CIPHERS_OVERRIDE ?? "");
  }, [setting.settings?.SSL_CIPHERS_OVERRIDE]);
  useEffect(() => {
    setDisableWeakCipherSuites(
      setting.settings?.DISABLE_WEAK_CIPHER_SUITES ?? "",
    );
  }, [setting.settings?.DISABLE_WEAK_CIPHER_SUITES]);
  useEffect(() => {
    setShowElbHealthchecks(setting.settings?.SHOW_ELB_HEALTHCHECKS ?? "");
  }, [setting.settings?.SHOW_ELB_HEALTHCHECKS]);
  useEffect(() => {
    setReleaseHealthcheckTimeout(
      setting.settings?.RELEASE_HEALTHCHECK_TIMEOUT ?? "",
    );
  }, [setting.settings?.RELEASE_HEALTHCHECK_TIMEOUT]);

  const idleTimeoutError = (() => {
    if (idleTimeout === "") return undefined;
    const n = Number(idleTimeout);
    if (!Number.isInteger(n) || n < 30 || n > 2400) {
      return "Must be between 30 and 2400 seconds";
    }
  })();
  const releaseHealthcheckTimeoutError = (() => {
    if (releaseHealthcheckTimeout === "") return undefined;
    const n = Number(releaseHealthcheckTimeout);
    if (!Number.isInteger(n) || n < 1 || n > 15 * 60) {
      return "Must be between 1 and 900 seconds";
    }
  })();
  const maintenancePageUrlError = (() => {
    if (maintenancePageUrl === "") return undefined;
    try {
      const u = new URL(maintenancePageUrl);
      if (u.protocol !== "http:" && u.protocol !== "https:") {
        return "Must be an http or https URL";
      }
    } catch {
      return "Must be a valid URL";
    }
  })();

  const isHttp = enp.type === "http" || enp.type === "http_proxy_protocol";
  const applies = (setting: string) =>
    endpointSettingApplies(setting, enp.type, enp.platform);

  const origSettings = setting.settings ?? {};

  // Include a setting only if the user changed it from the stored value.
  // Sending "" explicitly clears a previously-set value on the backend.
  const changed = (key: string, val: string) =>
    val !== (origSettings[key] ?? "");

  const newSettings: Record<string, string> = {
    ...(applies("FORCE_SSL") && changed("FORCE_SSL", forceSSL)
      ? { FORCE_SSL: forceSSL }
      : {}),
    ...(applies("IDLE_TIMEOUT") && changed("IDLE_TIMEOUT", idleTimeout)
      ? { IDLE_TIMEOUT: idleTimeout }
      : {}),
    ...(applies("MAINTENANCE_PAGE_URL") &&
    changed("MAINTENANCE_PAGE_URL", maintenancePageUrl)
      ? { MAINTENANCE_PAGE_URL: maintenancePageUrl }
      : {}),
    ...(applies("STRICT_HEALTH_CHECKS") &&
    changed("STRICT_HEALTH_CHECKS", strictHealthChecks)
      ? { STRICT_HEALTH_CHECKS: strictHealthChecks }
      : {}),
    ...(applies("SSL_PROTOCOLS_OVERRIDE") &&
    changed("SSL_PROTOCOLS_OVERRIDE", sslProtocolsOverride)
      ? { SSL_PROTOCOLS_OVERRIDE: sslProtocolsOverride }
      : {}),
    ...(applies("SSL_CIPHERS_OVERRIDE") &&
    changed("SSL_CIPHERS_OVERRIDE", sslCiphersOverride)
      ? { SSL_CIPHERS_OVERRIDE: sslCiphersOverride }
      : {}),
    ...(applies("DISABLE_WEAK_CIPHER_SUITES") &&
    changed("DISABLE_WEAK_CIPHER_SUITES", disableWeakCipherSuites)
      ? { DISABLE_WEAK_CIPHER_SUITES: disableWeakCipherSuites }
      : {}),
    ...(applies("SHOW_ELB_HEALTHCHECKS") &&
    changed("SHOW_ELB_HEALTHCHECKS", showElbHealthchecks)
      ? { SHOW_ELB_HEALTHCHECKS: showElbHealthchecks }
      : {}),
    ...(applies("RELEASE_HEALTHCHECK_TIMEOUT") &&
    changed("RELEASE_HEALTHCHECK_TIMEOUT", releaseHealthcheckTimeout)
      ? { RELEASE_HEALTHCHECK_TIMEOUT: releaseHealthcheckTimeout }
      : {}),
  };
  const settingsSame =
    JSON.stringify(Object.entries(origSettings).sort()) ===
    JSON.stringify(Object.entries(newSettings).sort());

  const data = {
    id: endpointId,
    ipAllowlist: parseIpStr(ipAllowlist),
    containerPort: port,
    containerPorts: parsePortsStrToNum(ports),
    certId: usingNewCert ? "" : certId,
    envId: service.environmentId,
    cert,
    privKey,
    tokenHeader: isHttp ? tokenHeader : undefined,
    lbAlgorithm: enp.type.startsWith("http") ? lbAlgorithm : undefined,
    shared: isHttp && enp.platform === "alb" ? shared : false,
    requiresCert: isRequiresCert(enp),
    enpType: enp.type,
    domain: enp.type.startsWith("http") && shared ? domain : undefined,
    settings: newSettings,
  };
  const ipsSame = origAllowlist === ipAllowlist;
  const portSame = enp.containerPort === port;
  const portsSame = origContainerPorts === ports;
  const certSame = enp.certificateId === certId;
  const tokenSame = enp.tokenHeader === tokenHeader;
  const lbAlgorithmSame = enp.lbAlgorithm === lbAlgorithm;
  const sharedSame = enp.shared === shared;
  const domainSame = enp.userDomain === domain;
  const isDisabled =
    ipsSame &&
    portSame &&
    portsSame &&
    certSame &&
    cert === "" &&
    tokenSame &&
    lbAlgorithmSame &&
    sharedSame &&
    domainSame &&
    settingsSame &&
    // Enable the button without changes if the endpoint is not provisioned to allow for retries
    enp.status === "provisioned";
  const curPortText = getContainerPort(enp, exposedPorts);
  const updateAction = updateEndpoint(data);
  const loader = useLoader(updateEndpoint);
  const [errors, validate] = useValidator<
    EndpointUpdateProps,
    typeof validators
  >(validators);

  const onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (isDisabled) return;
    if (!validate(data)) return;
    if (idleTimeoutError) return;
    if (releaseHealthcheckTimeoutError) return;
    if (maintenancePageUrlError) return;
    dispatch(updateAction);
  };

  useLoaderSuccess(loader, () => {
    navigate(endpointDetailActivityUrl(endpointId));
  });

  const certSelectorForm = (
    <FormGroup
      htmlFor="existing-cert"
      label="Certificate"
      feedbackMessage={errors.cert}
      feedbackVariant={errors.cert ? "danger" : "info"}
    >
      {usingNewCert ? null : (
        <CertSelector
          envId={app.environmentId}
          selectedId={certId}
          onSelect={(opt) => setCertId(opt.value)}
          className="mb-2"
        />
      )}
      <CheckBox
        label="Create a New Certificate"
        name="new-cert"
        checked={usingNewCert}
        onChange={(e) => setUsingNewCert(e.currentTarget.checked)}
      />
    </FormGroup>
  );

  const certForm = (
    <FormGroup
      htmlFor="cert"
      label="New Certificate"
      feedbackMessage={errors.cert}
      feedbackVariant={errors.cert ? "danger" : "info"}
      description={
        <>
          <p>
            Drag and drop your certificate file or paste its contents in the
            textarea below.
          </p>
          <p>
            If you have a bundled certificate chain, drag and drop all of the
            certificate files or paste their contents in the textarea below.
          </p>
        </>
      }
    >
      <FileDropTextArea
        id="cert"
        aria-label="cert"
        onValueChange={setCert}
        value={cert}
      />
    </FormGroup>
  );

  const privKeyForm = (
    <FormGroup
      htmlFor="private-key"
      label="Private Key for New Certificate"
      description="Drag and drop your private key file or paste its contents in the textarea below."
      feedbackMessage={errors.privKey}
      feedbackVariant={errors.privKey ? "danger" : "info"}
    >
      <FileDropTextArea
        id="private-key"
        aria-label="private-key"
        onValueChange={setPrivKey}
        value={privKey}
      />
    </FormGroup>
  );

  const certEditForm =
    service.appId && data.requiresCert ? (
      <>
        {certSelectorForm}
        {usingNewCert ? certForm : null}
        {usingNewCert ? privKeyForm : null}
      </>
    ) : null;

  const portForm = service.appId ? (
    isTlsOrTcp(enp) ? (
      <FormGroup
        label="Container Ports"
        description={`Current container ports: ${curPortText}`}
        htmlFor="ports"
        feedbackMessage={errors.ports}
        feedbackVariant={errors.ports ? "danger" : "info"}
      >
        <Input
          type="text"
          id="ports"
          name="ports"
          value={ports}
          onChange={(e) => setPorts(e.currentTarget.value)}
        />
      </FormGroup>
    ) : (
      <FormGroup
        label="Container Port"
        description={`Current container port: ${curPortText}`}
        htmlFor="port"
        feedbackMessage={errors.port}
        feedbackVariant={errors.port ? "danger" : "info"}
      >
        <Input
          type="text"
          id="port"
          name="port"
          value={port}
          onChange={(e) => setPort(e.currentTarget.value)}
        />
      </FormGroup>
    )
  ) : null;

  const tokenEditForm = isHttp ? (
    <FormGroup
      label="Header Authentication Value"
      htmlFor="token-header"
      description={`The 'X-Origin-Token' header value. When set, clients will be required to pass a
          'X-Origin-Token' header matching this value. Token header may only contain letters, numbers,
          '_', '-', ':', and '.'`}
    >
      <Input
        type="text"
        id="token-header"
        name="token-header"
        value={tokenHeader}
        onChange={(e) => setTokenHeader(e.currentTarget.value)}
      />
    </FormGroup>
  ) : null;

  const loadBalancingAlgorithmOptions: SelectOption[] = Object.keys(
    loadBalancingAlgorithms,
  ).map((alg) => ({ label: alg, value: loadBalancingAlgorithms[alg] }));

  const lbAlgorithmEditForm =
    enp.platform === "alb" ? (
      <FormGroup
        label="Load Balancing Algorithm"
        htmlFor="lb-algorithm"
        description="Choose the load balancing algorithm for this ALB endpoint."
      >
        <Select
          ariaLabel="Load Balancing Algorithm"
          id="lb-algorithm"
          value={lbAlgorithm}
          options={loadBalancingAlgorithmOptions}
          onSelect={(opt) => setLbAlgorithm(opt.value)}
        />
      </FormGroup>
    ) : null;

  let sharedFeedbackMessage: string | undefined;
  if (enp.shared) {
    sharedFeedbackMessage =
      "Shared endpoints cannot be converted to dedicated endpoints.";
  } else if (shared) {
    sharedFeedbackMessage =
      "Warning: Once saved, shared endpoints cannot be converted back to dedicated endpoints.";
  }

  const sharedEditForm =
    isHttp && enp.platform === "alb" ? (
      <FormGroup
        label="Shared Endpoint"
        htmlFor="shared"
        description={
          <>
            Share a load balancer across apps for a reduced per-endpoint rate.
            Highly recommended for non-production resources.{" "}
            <ExternalLink href="https://www.aptible.com/docs/core-concepts/apps/connecting-to-apps/app-endpoints/https-endpoints/shared-endpoints#shared-endpoints">
              Learn more
            </ExternalLink>
          </>
        }
        feedbackMessage={sharedFeedbackMessage}
        feedbackVariant={enp.shared ? undefined : "danger"}
      >
        <CheckBox
          label="Enable shared endpoint"
          name="shared"
          checked={shared ?? false}
          disabled={enp.shared ?? false}
          onChange={(e) => setShared(e.currentTarget.checked)}
        />
      </FormGroup>
    ) : null;

  const domainEditForm =
    enp.type.startsWith("http") &&
    shared &&
    !enp.shared &&
    data.requiresCert ? (
      <FormGroup
        label="Domain Name"
        htmlFor="domain"
        description="Wildcard domains are not supported."
        feedbackMessage={errors.domain}
        feedbackVariant={errors.domain ? "danger" : "info"}
      >
        <Input
          type="text"
          id="domain"
          name="domain"
          value={domain}
          onChange={(e) => setDomain(e.currentTarget.value)}
        />
      </FormGroup>
    ) : null;

  const securityForm = (
    <EndpointSecuritySettings
      applies={applies}
      sslProtocolsOptions={buildSslProtocolsOptions(
        isHttp && enp.platform === "alb",
      )}
      ipAllowlist={ipAllowlist}
      onIpAllowlistChange={setIpAllowlist}
      ipAllowlistError={errors.ipAllowlist}
      forceSSL={forceSSL}
      onForceSSLChange={setForceSSL}
      sslProtocolsOverride={sslProtocolsOverride}
      onSslProtocolsOverrideChange={setSslProtocolsOverride}
      sslCiphersOverride={sslCiphersOverride}
      onSslCiphersOverrideChange={setSslCiphersOverride}
      disableWeakCipherSuites={disableWeakCipherSuites}
      onDisableWeakCipherSuitesChange={setDisableWeakCipherSuites}
      tokenFormSlot={tokenEditForm}
    />
  );

  const healthchecksForm = (
    <EndpointHealthchecksSettings
      applies={applies}
      strictHealthChecks={strictHealthChecks}
      onStrictHealthChecksChange={setStrictHealthChecks}
      releaseHealthcheckTimeout={releaseHealthcheckTimeout}
      onReleaseHealthcheckTimeoutChange={setReleaseHealthcheckTimeout}
      releaseHealthcheckTimeoutError={releaseHealthcheckTimeoutError}
      idleTimeout={idleTimeout}
      onIdleTimeoutChange={setIdleTimeout}
      idleTimeoutError={idleTimeoutError}
      showElbHealthchecks={showElbHealthchecks}
      onShowElbHealthchecksChange={setShowElbHealthchecks}
      maintenancePageUrl={maintenancePageUrl}
      onMaintenancePageUrlChange={setMaintenancePageUrl}
      maintenancePageUrlError={maintenancePageUrlError}
      lbAlgorithmSlot={lbAlgorithmEditForm}
    />
  );

  return (
    <Box>
      <h1 className="text-lg text-gray-500 mb-4">Endpoint Settings</h1>
      <form onSubmit={onSubmit} className="flex flex-col gap-4">
        {portForm}
        {certEditForm}
        {sharedEditForm}
        {domainEditForm}
        <div className="border-y border-gray-200 divide-y divide-gray-200">
          {securityForm}
          {healthchecksForm}
        </div>

        <BannerMessages {...loader} />

        <ButtonCreate
          type="submit"
          envId={service.environmentId}
          isLoading={loader.isLoading}
          className="w-40"
          disabled={isDisabled}
        >
          Save Changes
        </ButtonCreate>
      </form>
    </Box>
  );
};

export const EndpointDetailSettingsPage = () => {
  const { id = "" } = useParams();
  const enp = useSelector((s) => selectEndpointById(s, { id }));
  const service = useSelector((s) =>
    selectServiceById(s, { id: enp.serviceId }),
  );

  return (
    <Group variant="vertical" size="lg">
      <EndpointSettings endpointId={id} />
      <EndpointDeprovision endpointId={id} envId={service.environmentId} />
    </Group>
  );
};
